/**
 * ThemeContext - Manages light/dark theme preferences.
 *
 * THE THEME IS STICKY WITHIN A VISIT. Once a theme is in effect it does
 * not change on in-app navigation. What decides it:
 *
 *   1. A choice made with the theme toggle wins, on every page, for
 *      good (EXPLICIT_KEY).
 *   2. Otherwise each page load starts from that route's default
 *      (marketing = light, workspace = dark), and the first workspace
 *      route reached during the visit switches to dark once, so someone
 *      who arrived via the marketing site still lands in the dark-first
 *      dashboard.
 *
 * (2) used to be once per BROWSER: the dark adopted in the dashboard
 * was stored and reused on every later load, so anyone whose browser
 * had ever opened a dashboard page (a host, or a visitor who clicked an
 * app link before signing up) saw canira.io's homepage in dark. A theme
 * the visitor never chose is no longer carried from one visit to the
 * next; only the toggle's choice is.
 *
 * Why it works this way: the theme used to be re-derived from the URL
 * on every navigation, against a hand-maintained allowlist of public
 * paths. In an app this size that list is impossible to keep correct —
 * /webinar/:id/hub (the post-create session overview) sat under the
 * public '/webinar/' prefix, so finishing the create flow visibly
 * kicked the creator out of dark mode. Deriving once removes that
 * whole class of bug instead of patching it path by path.
 *
 * The one exception is FORCED_LIGHT_PATHS (the auth, legal and help
 * pages), which render light regardless of preference — see that
 * constant.
 */

import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';

const ThemeContext = createContext();

// Storage keys
const THEME_KEY = 'canira-theme';            // the user's current theme preference
const EXPLICIT_KEY = 'canira-theme-explicit'; // 'true' once user toggles
const SEEDED_KEY = 'canira-theme-seeded';     // written when the workspace switch runs; no longer read

// localStorage access that cannot take the app down with it. Reading
// window.localStorage throws outright (SecurityError) in a browser with
// site data blocked, and writes throw when the store is full. This
// provider wraps the entire app, so an unguarded throw here is a white
// screen on every route, not a lost theme. Degrades to "no memory
// between loads", which is survivable.
const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (_) {
      /* no persistence available — the in-memory theme still works */
    }
  },
  remove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (_) {
      /* as above */
    }
  },
};

// React Router matches routes case-insensitively and ignores a trailing
// slash, but hands us location.pathname verbatim — so /TERMS and
// /login/ render those pages while missing an exact-match lookup here.
// Normalise before every comparison.
function normalizePath(pathname) {
  if (!pathname) return '';
  const lowered = String(pathname).toLowerCase();
  return lowered.length > 1 ? lowered.replace(/\/+$/, '') : lowered;
}

// Marketing/public routes that START light. Everything else
// (dashboard, /my-clips, /ai/*, /settings, /admin, etc.) starts dark.
// Kept as a small allowlist of public paths because the dashboard
// surface keeps growing — easier to maintain.
//
// SCOPE: this classification is consulted only for a visitor's first
// visit and for the one-time workspace seed. It is NOT re-applied on
// navigation, so a path missing from these lists no longer flips a
// user's theme mid-session — it can only affect where a brand-new
// visitor starts.
const PUBLIC_LIGHT_PATHS = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/beta',
  '/pricing',
  '/demo',
  '/features',
  '/help',
  '/unsubscribe',
  '/terms',
  '/privacy',
  '/contact',
  '/about',
  '/podcasts/browse',  // public podcast directory (the only public /podcasts page)
]);
const PUBLIC_LIGHT_PREFIXES = [
  '/help/',
  '/blog',
  '/affiliate',
  '/webinar/',  // public webinar viewer pages
  '/podcast/',  // public podcast viewer pages (singular, e.g. /podcast/:slug)
  // NOTE: do NOT add '/podcasts/' (plural) — those are authenticated podcast
  // management pages (dashboard, episode create/edit, settings) and must
  // default to DARK like the rest of the workspace. The one public plural
  // page, /podcasts/browse, is listed in PUBLIC_LIGHT_PATHS above.
  '/u/',        // public user/share pages
];

// Authenticated workspace routes that LOOK public to the prefix match
// above but are not. These must stay dark like the rest of the
// dashboard, so they're checked FIRST and short-circuit the light
// prefixes.
//
// Why this list exists: creating a session navigates to
// /webinar/:id/hub (the post-create overview). That path starts with
// the public '/webinar/' prefix, so the route default flipped to
// light the moment the creator landed on their new session — the
// dashboard visibly switched out of dark mode mid-flow. Same story
// for /affiliates, which the '/affiliate' marketing prefix swallowed.
//
// Add a pattern here whenever a dashboard route is nested under a
// path segment that also serves public pages.
const DASHBOARD_DARK_PATTERNS = [
  /^\/webinar\/[^/]+\/hub(\/|\?|$)/,  // per-webinar Hub / overview (host-only)
  /^\/affiliates(\/|\?|$)/,           // affiliate dashboard (host-only)
];

function isDashboardDarkPath(pathname) {
  const path = normalizePath(pathname);
  if (!path) return false;
  return DASHBOARD_DARK_PATTERNS.some((pattern) => pattern.test(path));
}

export function isPublicLightPath(pathname) {
  const path = normalizePath(pathname);
  if (!path) return false;
  if (isDashboardDarkPath(path)) return false;
  if (PUBLIC_LIGHT_PATHS.has(path)) return true;
  return PUBLIC_LIGHT_PREFIXES.some((prefix) => path.startsWith(prefix));
}

// Pages a host's STUDENTS use — the course sales page, the learning page,
// the course catalog and a creator's course list. Students aren't Canira
// users, so instead of the dark-first workspace default these follow the
// student's device (light/dark setting) until they pick with the switch on
// the page. They also never trigger the workspace dark switch.
const STUDENT_PATH_PATTERNS = [
  /^\/course\//,                       // /course/catalog, /course/:id, /course/:id/learn
  /^\/creator\/[^/]+\/courses(\/|$)/,   // a creator's public course list
];

export function isStudentPath(pathname) {
  const path = normalizePath(pathname);
  if (!path) return false;
  return STUDENT_PATH_PATTERNS.some((pattern) => pattern.test(path));
}

function deviceTheme() {
  try {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)')?.matches === true
      ? 'dark'
      : 'light';
  } catch (_) {
    return 'light';
  }
}

export function getRouteDefaultTheme(pathname) {
  if (isStudentPath(pathname)) return deviceTheme();
  return isPublicLightPath(pathname) ? 'light' : 'dark';
}

// Routes that render LIGHT regardless of the user's stored preference.
// These are the hand-painted brand pages: their stylesheets define no
// .theme-dark rules, so they cannot follow the theme even when asked.
//
// Two different symptoms, one fix:
//   - The auth surface (AuthPremium.css) reads ~21 theme CSS variables
//     but defines no dark overrides, so in dark mode it renders a
//     half-dark page — themed text and inputs over hardcoded light
//     panels.
//   - The legal and help pages (LegalPages.css, HelpCenter.css,
//     HelpArticle.css) use no theme variables at all, so the page body
//     stayed light while the shared Navigation above it (68 variable
//     uses) went dark — dark chrome capping a white page.
//
// Pinning affects rendering only — it never writes to the user's
// stored preference, so a dark-mode user passing through /login or
// /terms is still in dark mode on the other side. ThemeToggle stays
// visible on these routes and reads `preference` (not the painted
// theme) via isThemePinned, so a switch made here still registers and
// takes effect as soon as the user leaves.
const FORCED_LIGHT_PATHS = new Set([
  // Auth surface
  '/login',
  '/login/2fa',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/resend-verification',
  '/auth/callback',
  // Legal / policy (all LegalPages.css)
  '/terms',
  '/privacy',
  '/dmca',
  '/data-processing',
  '/data-deletion',
  '/delete',
  '/delete-account',
  '/sms-opt-in',
  '/trust',
  '/security',
  // Help centre
  '/help',
  // Unsubscribe page (hand-painted light, no theme rules)
  '/unsubscribe',
]);

// Prefix form, for the parameterised routes above.
const FORCED_LIGHT_PREFIXES = [
  '/help/',  // /help/:slug — individual help articles
];

export function isForcedLightPath(pathname) {
  const path = normalizePath(pathname);
  if (!path) return false;
  if (FORCED_LIGHT_PATHS.has(path)) return true;
  return FORCED_LIGHT_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // The user's stored preference. Derived from the route ONLY on a
  // first visit with nothing stored; from then on it changes only via
  // the toggle (or the one-time workspace seed in syncToRoute).
  const getInitialPreference = () => {
    if (typeof window === 'undefined') return 'light';
    // A stored preference always wins — explicit or not. This is what
    // makes the theme survive navigation and hard refreshes instead of
    // being recomputed from the URL on every route change.
    // Only a choice the visitor made with the toggle carries between
    // visits. A stored theme without that flag was picked for them by
    // the workspace switch below, and must not turn the homepage dark.
    const saved = storage.get(THEME_KEY);
    if ((saved === 'light' || saved === 'dark') && storage.get(EXPLICIT_KEY) === 'true') return saved;
    // Otherwise the page the visit starts on picks the starting theme.
    return getRouteDefaultTheme(window.location.pathname);
  };

  const [theme, setTheme] = useState(getInitialPreference);

  // Current pathname, fed by ThemeRouteSync. Used only to decide
  // whether this route pins the rendered theme to light.
  const [routePath, setRoutePath] = useState(() =>
    (typeof window === 'undefined' ? '' : window.location.pathname)
  );

  // Mirrors the EXPLICIT/SEEDED flags in memory. The seed guards below
  // read those flags back out of storage, so when storage is
  // unavailable (site data blocked) every write silently fails, every
  // read returns null, and the "once per browser" seed re-fires on the
  // next workspace route — overwriting the choice the user just made
  // with the toggle. The ref keeps the guard honest for the life of
  // the page even with nothing to persist to.
  // Settled means "the workspace switch must not run": an explicit
  // choice settles it for good, and the switch itself settles it for
  // the rest of this visit only (it is not read back from storage).
  const settledRef = useRef(undefined);
  if (settledRef.current === undefined) {
    settledRef.current = storage.get(EXPLICIT_KEY) === 'true';
  }

  const isThemePinned = isForcedLightPath(routePath);
  // What actually gets painted. Differs from `theme` only on the
  // pinned auth routes.
  const effectiveTheme = isThemePinned ? 'light' : theme;

  // Apply the theme class to the document root. useLayoutEffect so the
  // <html> class is updated synchronously before paint, which keeps the
  // pinned auth routes from flashing dark on entry.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.remove('theme-light', 'theme-dark');
    root.classList.add(`theme-${effectiveTheme}`);
    root.setAttribute('data-theme', effectiveTheme);
  }, [effectiveTheme]);

  // Persist the PREFERENCE, never what is on screen. A dark-mode user
  // passing through the pinned-light /login must still be in dark mode
  // on the other side, so effectiveTheme must not be written here.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    storage.set(THEME_KEY, theme);
  }, [theme]);

  // Called by ThemeRouteSync on every pathname change. It records the
  // route (for pinning) and runs the one-time workspace seed. It does
  // NOT otherwise touch the theme — that stickiness is the point.
  const syncToRoute = useCallback((pathname) => {
    if (typeof window === 'undefined') return;
    setRoutePath(pathname);

    // Workspace switch, once per visit: the first time this visitor
    // reaches a workspace route, adopt dark. Without it, someone who
    // arrived on the (light) marketing site and then signed in would
    // carry light into a dashboard that is designed dark-first. Never
    // runs against an explicit choice, and never twice in one visit.
    if (settledRef.current) return;
    if (isStudentPath(pathname)) return; // students follow their device, not the workspace
    if (getRouteDefaultTheme(pathname) !== 'dark') return;
    settledRef.current = true;
    storage.set(SEEDED_KEY, 'true');
    setTheme('dark');
  }, []);

  // Marks the theme as user-chosen. Used by the toggle / setters below.
  const markExplicit = useCallback(() => {
    if (typeof window === 'undefined') return;
    settledRef.current = true;
    storage.set(EXPLICIT_KEY, 'true');
    // A deliberate choice also settles the workspace seed — otherwise a
    // user who picks light on a marketing page would be flipped to dark
    // the first time they opened the dashboard.
    storage.set(SEEDED_KEY, 'true');
  }, []);

  // NOTE: there is deliberately no prefers-color-scheme listener. It
  // used to flip the theme mid-session when the OS switched, which is
  // the same "the theme changed and I didn't ask for it" behaviour this
  // context now exists to prevent. The OS preference was never the
  // source of the initial theme (only of that live flip), so nothing
  // else depended on it.

  const toggleTheme = () => {
    markExplicit();
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };
  const setLightTheme = () => {
    markExplicit();
    setTheme('light');
  };
  const setDarkTheme = () => {
    markExplicit();
    setTheme('dark');
  };

  // Public escape hatch — settings page could expose "Reset to
  // route default" if we ever want it. Not wired into UI yet.
  const clearExplicitPreference = () => {
    if (typeof window === 'undefined') return;
    settledRef.current = false;
    storage.remove(EXPLICIT_KEY);
    storage.remove(SEEDED_KEY);
    setTheme(getRouteDefaultTheme(window.location.pathname));
  };

  const value = {
    // `theme` is what is rendered, so consumers that stamp a
    // theme-${theme} class follow the pin on auth routes.
    theme: effectiveTheme,
    isDark: effectiveTheme === 'dark',
    isLight: effectiveTheme === 'light',
    // The stored preference, ignoring any pin. Exposed for the toggle
    // and for anything that needs the user's actual choice.
    preference: theme,
    isThemePinned,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    setTheme,
    syncToRoute,
    clearExplicitPreference,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
